import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ReactElement, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ImageUploadIcon from "../../../assets/Fibo/icons/ImageUploadIcon";
import { LABS } from "../../../constants/AppsConstants.ts";
import { useFileDrop } from "../../../hooks/useFileDrop.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { PlaygroundResult } from "../../../models/image-to-image.ts";
import { TextToImageConfigType } from "../../../models/text-to-image.ts";
import { useScrollToSection } from "../../../pages/Fibo/hooks/useScrollToSection.tsx";
import FiboGenerateResult from "../../../pages/Fibo/Images/FiboGenerateResult.tsx";
import FiboResultHeaderActions from "../../../pages/Fibo/Images/FiboResultHeader.tsx";
import FiboResultInfo from "../../../pages/Fibo/Images/FiboResultInfo.tsx";
import FiboEditResult from "../../../pages/FiboEdit/Result/FiboEditResult.tsx";
import VideoEditingResult from "../../../pages/Labs/VideoEditing/Result/VideoEditingResult.tsx";
import styles from "./LabResults.module.scss";

const LabResultsRenderer = ({
	result,
	extraResultInfo,
	resultHeaderActions,
}: {
	result: PlaygroundResult;
	extraResultInfo: ReactElement | null;
	resultHeaderActions: ReactElement | null;
}) => {
	switch (result.type) {
		case LABS.FIBO_EDIT:
			return (
				<FiboEditResult
					playgroundResult={result}
					extraResultInfo={extraResultInfo}
					resultHeaderActions={resultHeaderActions}
				/>
			);

		case LABS.VIDEO_EDITING:
			return <VideoEditingResult playgroundResult={result} />;

		case LABS.FIBO:
			return (
				<FiboGenerateResult
					playgroundResult={result}
					extraResultInfo={extraResultInfo}
					resultHeaderActions={resultHeaderActions}
				/>
			);

		default:
			return null;
	}
};

type LabResultsProps = {
	emptyElement?: ReactElement | null;
	onResultInView?: (id: string) => void;
	registerSection: (id: string, element: HTMLDivElement | null) => void;
	onReferenceImageClick: (imageUrl: string) => void;
	onUpload?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
	acceptedFileTypes?: string;
};

const LabResults = ({
	onResultInView,
	registerSection,
	onReferenceImageClick,
	emptyElement,
	onUpload,
	acceptedFileTypes = "image/png, image/jpeg, image/jpg, image/webp",
}: LabResultsProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.images" });
	const { t: tDragDrop } = useTranslation("translation", { keyPrefix: "playground.dragAndDropOverlay" });
	const { playgroundStore, textToImageStore, fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const results = playgroundStore.playgroundResults;
	const { setupIntersectionObserver } = useScrollToSection();
	const containerRef = useRef<HTMLDivElement>(null);
	const prevResultsLengthRef = useRef(results.length);
	const reversedResults = useMemo(() => [...results].reverse(), [results]);

	const onFileDropped = useCallback(
		async (files: File[]) => {
			if (!files.length || !onUpload) return;

			const acceptedTypes = acceptedFileTypes.split(",").map((t) => t.trim());
			const fileArray = files.filter((file) =>
				acceptedTypes.some((type) => file.type === type || file.type.startsWith(type.split("/")[0] + "/")),
			);

			if (!fileArray.length) return;

			const syntheticEvent = {
				target: { files: fileArray, value: "" },
			} as unknown as React.ChangeEvent<HTMLInputElement>;

			await onUpload(syntheticEvent);
		},
		[onUpload, acceptedFileTypes],
	);

	const { isDragging, dropHandlers } = useFileDrop({
		onDropped: onFileDropped,
		enabled: !!onUpload,
		cancelOnEscape: true,
	});

	useEffect(() => {
		const cleanup = setupIntersectionObserver(onResultInView);
		return cleanup;
	}, [onResultInView, setupIntersectionObserver]);

	useEffect(() => {
		const currentLength = results.length;
		const prevLength = prevResultsLengthRef.current;

		if (currentLength > prevLength && currentLength > 0) {
			const newestResult = results[currentLength - 1];
			const hasLoadingImages = newestResult.images.some((image) => image.loading);

			if (hasLoadingImages && containerRef.current) {
				containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
			}
		}

		prevResultsLengthRef.current = currentLength;
	}, [results.length, results]);

	if (!results.length) {
		return (
			<Box className={styles.emptyContainer}>
				{emptyElement ?? <Box className={styles.defaultEmptyContent} />}
			</Box>
		);
	}

	return (
		<Box className={styles.fiboImagesView} {...dropHandlers}>
			{isDragging && (
				<Box className={styles.dragOverlay}>
					<Box className={styles.overlayIcon}>
						<ImageUploadIcon />
					</Box>
					<Typography className={styles.dragText}>{tDragDrop("dragText")}</Typography>
				</Box>
			)}
			<Box ref={containerRef} className={styles.container}>
				{reversedResults.map((result) => {
					if (!result.images.length && !result.video) return null;

					const isRefine = (result.config as any)?.is_refine;
					const prompt = (result.config as TextToImageConfigType)?.prompt ?? "";
					const refImageUrl = result.images.find((img) => img.ref_image_url)?.ref_image_url;
					const mode = isRefine ? t("refine") : refImageUrl ? t("inspiredGenerate") : t("generate");

					const handleReusePrompt = (prompt: string) => {
						if (result.type === LABS.FIBO_EDIT) {
							fiboEditStore.config.prompt = prompt;
						} else {
							textToImageStore.handleConfigChange("prompt", prompt);
						}
					};

					const extraResultInfo = (
						<FiboResultInfo
							config={result.config as TextToImageConfigType}
							mode={mode}
							modeLabel={t("mode")}
							refImageUrl={refImageUrl}
							onReferenceImageClick={onReferenceImageClick}
							resultId={result.id}
						/>
					);

					const resultHeader = (
						<FiboResultHeaderActions
							prompt={prompt}
							copyPromptTooltip={t("copyPrompt")}
							reusePromptTooltip={t("reusePrompt")}
							onReusePrompt={handleReusePrompt}
						/>
					);

					return (
						<Box
							key={result.id}
							data-result-id={result.id}
							ref={(el) => registerSection(result.id, el as HTMLDivElement)}
							className={styles.groupSection}
						>
							<LabResultsRenderer
								result={result}
								extraResultInfo={extraResultInfo}
								resultHeaderActions={resultHeader}
							/>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

export default observer(LabResults);
